import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Treatment, Doctor, Hospital } from "@/types/schema";
import { treatmentData } from "@/data/treatmentData";

export interface TreatmentWithRelations extends Treatment {
  doctors: Doctor[];
  hospitals: Hospital[];
}

// Load all generated treatment JSONs for static access
const localTreatmentImports = import.meta.glob('../data/generated/treatments/*.json', { import: 'default' });

async function fetchTreatmentDetail(slug: string): Promise<TreatmentWithRelations | null> {
  // A. Try Local Static JSON (Prerender/Fast Path)
  const localKey = `../data/generated/treatments/${slug}.json`;
  const localLoader = localTreatmentImports[localKey];

  if (localLoader) {
    try {
      const data = await localLoader() as TreatmentWithRelations;
      const localOverride = treatmentData[slug] || {};
      
      return {
        ...data,
        ...localOverride,
        description: data.description || localOverride.procedure_details || data.description,
      } as TreatmentWithRelations;
    } catch (e) {
      console.warn(`Failed to load local treatment for ${slug}`, e);
    }
  }

  // B. Fallback logic removed as requested by user ("remove supabase call")
  console.warn(`Treatment data for ${slug} not found in static data.`);
  return null;
}

export function useTreatmentDetail(slug: string | undefined) {
  return useQuery({
    queryKey: ["treatment-detail", slug],
    queryFn: () => fetchTreatmentDetail(slug!),
    enabled: !!slug,
    staleTime: Infinity, // Data is static, no need to refetch
    gcTime: 1000 * 60 * 60, // 1 hour gc
  });
}
